import type { IconButtonProps } from '@mui/material/IconButton';

import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { fData } from 'src/utils/format-number';

import { fileData, FileThumbnail } from 'src/components/file-thumbnail';

import { Iconify } from '../../iconify';
import { uploadClasses } from '../classes';
import { ItemRow } from './preview-multi-file';

import type { SingleFilePreviewProps, SingleFilePreviewWithThumbnailProps } from '../types';

// ----------------------------------------------------------------------

export function SingleFilePreview({ file, sx, className, ...other }: SingleFilePreviewProps) {
  const fileName = typeof file === 'string' ? file : file.name;

  const previewUrl = typeof file === 'string' ? file : URL.createObjectURL(file);

  return (
    <PreviewRoot
      className={mergeClasses([uploadClasses.uploadSinglePreview, className])}
      sx={sx}
      {...other}
    >
      <img alt={fileName} src={previewUrl} />
    </PreviewRoot>
  );
}

/**
 * SingleFilePreviewWithThumbnail
 *
 * Use this component when you want to preview a single file with a thumbnail
 * and without the ability to upload a file.
 *
 * @example
 * ```tsx
 * <DialogContent sx={{ p: 2 }}>
 *   <Box component="div" sx={{ position: 'relative', minHeight: '100px' }}>
 *     {loading ? (
 *       <Box
 *         component="div"
 *         sx={{
 *           display: 'flex',
 *           justifyContent: 'center',
 *           alignItems: 'center',
 *           flexDirection: 'column',
 *         }}
 *       >
 *         <FileDropAnimationIcon sx={{ minHeight: '300px', minWidth: '300px' }} />
 *         <LoadingComponent />
 *       </Box>
 *     ) : (
 *       <Stack spacing={2}>
 *         {file.file ? (
 *           <SingleFilePreviewWithThumbnail
 *             file={file.file || ''}
 *             onRemove={handleRemoveFile}
 *           />
 *         ) : (
 *           <Upload
 *             value={file.file || undefined}
 *             onDrop={handleDrop}
 *             placeholderDescription="The file size must be 100 MB or less. Only .pdf, .doc, .docx or .zip files are allowed."
 *           />
 *         )}
 *       </Stack>
 *     )}
 *   </Box>
 * </DialogContent>
 * ```
 */
export function SingleFilePreviewWithThumbnail({
  file,
  sx,
  onRemove,
  slotProps,
  className,
  ...other
}: SingleFilePreviewWithThumbnailProps) {
  const { name, size } = fileData(file);
  return (
    <PreviewRoot
      className={mergeClasses([uploadClasses.uploadSinglePreview, className])}
      sx={sx}
      {...other}
    >
      <ItemRow>
        <FileThumbnail file={file} {...slotProps?.thumbnail} />

        <ListItemText
          primary={name}
          secondary={fData(size)}
          slotProps={{
            secondary: { sx: { typography: 'caption' } },
          }}
        />

        {onRemove && (
          <IconButton size="small" onClick={() => onRemove(file)}>
            <Iconify width={16} icon="mingcute:close-line" />
          </IconButton>
        )}
      </ItemRow>
    </PreviewRoot>
  );
}

// ----------------------------------------------------------------------

const PreviewRoot = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  width: '100%',
  position: 'absolute',
  padding: theme.spacing(1),
}));

// ----------------------------------------------------------------------

export function DeleteButton({ sx, ...other }: IconButtonProps) {
  return (
    <IconButton
      size="small"
      sx={[
        (theme) => ({
          top: 16,
          right: 16,
          zIndex: 9,
          position: 'absolute',
          color: varAlpha(theme.vars.palette.common.whiteChannel, 0.8),
          bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.72),
          '&:hover': { bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.48) },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Iconify icon="mingcute:close-line" width={18} />
    </IconButton>
  );
}
